import React from 'react';
import { HorizontalFlex, icons } from 'smartsuite-ui';
import PropTypes from 'prop-types';
import { useLabelContext } from '../../shared/hooks/labelContext';
import { getFormattedValue } from '../dashboardUtils';

// eslint-disable-next-line complexity
function shouldShowTooltip(activeIndex, payload) {
    return activeIndex !== -1 && payload && payload[activeIndex];
}

// eslint-disable-next-line complexity
export default function CustomTooltip({
    activeIndex,
    axisFormatter,
    axisKey,
    dataFormat,
    dataFormatter,
    legendFormatter,
    payload,
    showSinglePoint,
    suffix,
    thresholds,
}) {
    const { dashboardLabels } = useLabelContext();
    const displaySinglePayload = showSinglePoint;

    // eslint-disable-next-line complexity
    function renderTargets() {
        if (!thresholds?.length) {
            return null;
        }

        return thresholds.map((x) => {
            const value = getFormattedValue(
                x.value,
                suffix,
                dataFormatter,
                dataFormat
            );

            return (
                <HorizontalFlex
                    key={value}
                    cssClassModifiers={['align-center']}
                >
                    <span
                        style={{ color: x.color ?? x.lineColor }}
                        className={`${icons.target} custom-tooltip__target-icon`}
                    />
                    <span>
                        {x.label ?? dashboardLabels.target} : {value}
                    </span>
                </HorizontalFlex>
            );
        });
    }

    function renderSinglePointLabel(label, value, backgroundColor) {
        if (!label) {
            return <span>{value}</span>;
        }

        return (
            <HorizontalFlex>
                <span
                    style={{ backgroundColor }}
                    className="custom-tooltip__color-indicator"
                />
                <span>{`${label}: ${value}`}</span>
            </HorizontalFlex>
        );
    }

    function renderSinglePoint() {
        const allProperties = {
            ...payload[activeIndex],
            ...payload[activeIndex].payload,
        };

        const header = axisFormatter(allProperties[axisKey]);
        const label = legendFormatter(allProperties.dataKey, null, activeIndex);
        const dataValue = getFormattedValue(
            allProperties.value,
            suffix,
            dataFormatter,
            dataFormat
        );

        return (
            <div className="custom-tooltip">
                <div>{header}</div>
                {renderSinglePointLabel(label, dataValue, allProperties.color)}
                {renderTargets()}
            </div>
        );
    }

    function renderMultiplePoints() {
        const multiplePointsHeader = axisFormatter(
            payload[0].payload?.[axisKey]
        );

        const labels = payload.map((x, index) => {
            const allProperties = {
                ...x,
                ...x.payload,
            };

            const label = legendFormatter(x.dataKey, null, index);
            const dataValue = getFormattedValue(
                allProperties.value,
                suffix,
                dataFormatter,
                dataFormat
            );

            return (
                <HorizontalFlex key={label}>
                    <span
                        style={{ backgroundColor: allProperties.color }}
                        className="custom-tooltip__color-indicator"
                    />
                    <span>
                        {label}: {dataValue}
                    </span>
                </HorizontalFlex>
            );
        });

        return (
            <div className="custom-tooltip">
                <div>{multiplePointsHeader}</div>
                {labels}
                {renderTargets()}
            </div>
        );
    }

    if (!shouldShowTooltip(activeIndex, payload)) {
        return null;
    }

    return displaySinglePayload ? renderSinglePoint() : renderMultiplePoints();
}

CustomTooltip.propTypes = {
    activeIndex: PropTypes.number.isRequired,
    axisKey: PropTypes.string,
    axisFormatter: PropTypes.func,
    dataFormat: PropTypes.string,
    dataFormatter: PropTypes.func,
    legendFormatter: PropTypes.func,
    payload: PropTypes.instanceOf(Array),
    showSinglePoint: PropTypes.bool,
    suffix: PropTypes.string,
    thresholds: PropTypes.instanceOf(Array),
};

CustomTooltip.defaultProps = {
    axisKey: null,
    axisFormatter: (value) => value,
    dataFormat: null,
    dataFormatter: null,
    legendFormatter: (value) => value,
    payload: [],
    showSinglePoint: false,
    suffix: '',
};
